import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@socialchorus/shared-ui-components';
import { useChatbotThreadMessagesQuery } from '../../common/queries/use-chatbot-query';
import MessageBubble from './MessageBubble';
import PlaceholderIndicator from './PlaceholderIndicator';
import ChatSuggestions from './ChatSuggestions';
import { Suggestion } from '../../models/chatbot/types';

interface ChatMessagesProps {
  threadId?: string;
  isThreadPersisted?: boolean;
  programId: string;
  suggestions?: Suggestion[];
  onSuggestionClick?: (question: string) => void;
}

const ChatMessages: React.FC<ChatMessagesProps> = ({
  threadId,
  isThreadPersisted = false,
  programId,
  suggestions = [],
  onSuggestionClick,
}) => {
  const { t } = useTranslation();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: messages, isLoading } = useChatbotThreadMessagesQuery(
    programId,
    threadId,
    { enabled: !!threadId && !!programId && isThreadPersisted }
  );

  const displayMessages = messages || [];

  // Auto-scroll to bottom whenever messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  if (isLoading && threadId && isThreadPersisted) {
    return (
      <div className="chatbot__loading chatbot__loading--centered">
        <div className="chatbot__loading-content">
          <div className="spinner"></div>
          <div className="chatbot__loading-text">{t('chatbot.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="chatbot__messages">
      <div className="chatbot__messages-inner">
        <div className="chatbot__messages-content">
          {displayMessages.length === 0 ? (
            // Show empty state with suggestions at bottom
            <>
              <div className="chatbot__empty-state">
                <div className="chatbot__empty-state-icon">
                  <Icon size={32}>chat</Icon>
                </div>
                <h3 className="chatbot__empty-state-title">
                  {t('chatbot.emptyState.title')}
                </h3>
                <p className="chatbot__empty-state-description">
                  {t('chatbot.emptyState.description')}
                </p>
              </div>

              {/* Show suggestions near the bottom if available */}
              {suggestions.length > 0 && onSuggestionClick && (
                <div className="chatbot__suggestions-wrapper">
                  <ChatSuggestions
                    suggestions={suggestions}
                    onSuggestionClick={onSuggestionClick}
                  />
                </div>
              )}
            </>
          ) : (
            // Show actual messages
            displayMessages.map((message) => {
              // Hide message bubble if assistant has no content yet (while placeholder is showing)
              const shouldShowBubble =
                message.role === 'user' ||
                (message.role === 'assistant' && message.content);

              return (
                <div key={message.id}>
                  {/* Show placeholder before assistant messages when available */}
                  {message.role === 'assistant' && message.placeholderText && (
                    <PlaceholderIndicator text={message.placeholderText} />
                  )}

                  {shouldShowBubble && (
                    <MessageBubble
                      message={message}
                      isStreaming={message.isStreaming || false}
                    />
                  )}
                </div>
              );
            })
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
  );
};

export default ChatMessages;
